import { cn } from '@veraclins-dev/utils';

// The focus ring colour, in one place. An opaque token, because a translucent
// ring fails the 3:1 focus contrast on the page, card and popover surfaces
// (`scripts/verify-token-contrast.mjs` measures it). Width and offset stay with
// each control, as they vary by shape.
const FOCUS_RING_COLOR_CLASSES = 'focus-visible:ring-primary';

// The same colour for a control that draws focus as an outline, not a ring.
const FOCUS_OUTLINE_COLOR_CLASSES = 'focus-visible:outline-primary';
const FOCUS_WITHIN_OUTLINE_COLOR_CLASSES = 'focus-within:outline-primary';

// The whole default ring: colour, 2px width, and the 1px gap.
const FOCUS_RING_CLASSES = cn(
  FOCUS_RING_COLOR_CLASSES,
  'focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-background',
);

// A form field draws its ring inside its border box (`ring-inset`), where no
// scrolling container can clip it. A 1px ring with no gap: the border turns
// primary too, so the edge reads as one 2px line.
const FIELD_FOCUS_RING_CLASSES = cn(
  FOCUS_RING_COLOR_CLASSES,
  'ring-inset focus-visible:ring-1',
);

// The field ring on `:focus`, for the OTP slot, so a click shows it too.
const FIELD_FOCUS_RING_ALWAYS_CLASSES =
  'ring-inset focus:ring-1 focus:ring-primary';

// The inset ring on `:focus` for the checkbox and radio. The 2px gap, in the
// page colour, is what shows on a checked box filled with the ring's colour.
const FOCUS_RING_ALWAYS_CLASSES =
  'ring-inset focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background';

// The field ring for a container around a focusable child.
const FOCUS_WITHIN_RING_CLASSES =
  'ring-inset has-focus-visible:ring-1 has-focus-visible:ring-primary';

// Ring colour per tone. The control sets its own ring width.
const FOCUS_RING_TONE_CLASSES = {
  neutral: 'focus-visible:ring-neutral',
  brand: 'focus-visible:ring-primary',
  danger: 'focus-visible:ring-danger',
  success: 'focus-visible:ring-success',
  warning: 'focus-visible:ring-warning',
  info: 'focus-visible:ring-info',
} as const;

// The `tone` variant map for Button, Link, Toggle and StatusButton.
const ACTION_TONE_RING_CLASSES = {
  neutral: FOCUS_RING_TONE_CLASSES.neutral,
  brand: FOCUS_RING_TONE_CLASSES.brand,
  danger: FOCUS_RING_TONE_CLASSES.danger,
} as const;

const BASE_INPUT_CLASSES = cn(
  // Border and Shadow
  'border border-border-control shadow-xs',
  // Focus Styles. Each control sets its own ring: a `focus-visible:` width here
  // would override the checkbox's `focus:` one.
  'focus-visible:border-primary',
  // Disabled Styles
  'disabled:cursor-not-allowed disabled:opacity-50',
  // Error States. Both rules land after the focus rules at the same
  // specificity, so an invalid field keeps its danger colours while focused.
  // `data-[state=checked]:border-primary` on the checkbox and radio is emitted
  // last and still wins the border.
  'aria-invalid:ring-danger aria-invalid:border-danger',
  // Base
  'outline-hidden',
  // Transitions
  'transition-[color,box-shadow]',
);

// Press feedback: the transition and an `:active` scale, dropped under
// `prefers-reduced-motion`. Preflight clears the tap highlight and `hover:`
// sits behind `@media (hover: hover)`, so `:active` is the only feedback a tap
// gets.
//
// No `transition-*` may follow it in a composition: tailwind-merge keeps only
// the last transition, so a later `transition-shadow` drops the scale easing.
// Neither the `disabled` attribute nor `data-disabled="true"` shrinks (a
// disabled Button in a Tooltip uses the data attribute). The reduced-motion
// class repeats both guards to match the specificity of the class it undoes.
const PRESS_FEEDBACK_CLASSES = cn(
  'transition-[color,background-color,border-color,box-shadow,scale] duration-150 ease-out active:not-disabled:not-data-[disabled=true]:scale-96 motion-reduce:active:not-disabled:not-data-[disabled=true]:scale-100',
);

const CHECKBOX_CLASSES = cn(
  BASE_INPUT_CLASSES,
  PRESS_FEEDBACK_CLASSES,
  // Layout and Base
  'peer size-5 shrink-0 rounded-[4px]',
  // Text and Selection
  'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=checked]:border-primary',
  // Focus Styles. Rings on plain `:focus`, so the border follows the same trigger.
  FOCUS_RING_ALWAYS_CLASSES,
  'focus:border-primary',
);

const RADIO_GROUP_ITEM_CLASSES = cn(
  BASE_INPUT_CLASSES,
  PRESS_FEEDBACK_CLASSES,
  // Layout and Base
  'flex items-center justify-center aspect-square size-5 shrink-0 rounded-full',
  // checked
  'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary',
  // Focus Styles
  FOCUS_RING_ALWAYS_CLASSES,
  'focus:border-primary',
);

const INPUT_CLASSES = cn(
  BASE_INPUT_CLASSES,
  // Layout and Base
  'flex flex-1 min-w-24 bg-transparent',
  // Text and Selection. Placeholders use the opaque muted token (contrast check).
  'text-base data-[placeholder]:text-neutral-muted-foreground md:text-sm placeholder:text-neutral-muted-foreground selection:bg-primary selection:text-primary-foreground',
  // Border and Shadow
  'rounded-md',
  // Focus Styles. With an adornment this lands on a wrapper around the control.
  FIELD_FOCUS_RING_CLASSES,
  FOCUS_WITHIN_RING_CLASSES,
  'has-focus-visible:border-primary',
  // Disabled Styles
  'disabled:pointer-events-none',
  // File Input Styles
  'file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium',
  // SVG Styles
  '[&_svg]:shrink-0',
);

// Input Container Class
const INPUT_CONTAINER_CLASSES = cn(
  // Layout and Base
  'flex flex-1 min-w-0 bg-transparent outline-hidden items-center',
  // Spacing
  'px-3 py-2',
  // Border and Shadow
  'rounded-md border border-border-control shadow-xs',
  // Focus Styles
  FOCUS_WITHIN_RING_CLASSES,
  'has-focus-visible:border-primary',
  // Disabled Styles
  'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
  // Error States
  'has-aria-invalid:ring-danger has-aria-invalid:border-danger',
  // Transitions
  'transition-[border-color,box-shadow]',
);

// Input Class Overrides (applied to container to target input child)
const INPUT_CLASS_OVERRIDES = cn(
  // Spacing
  'p-0 min-h-0',
  // Border and Shadow
  'border-none rounded-none shadow-none',
  // Focus Styles
  'focus-visible:border-none focus-visible:ring-0 focus:ring-offset-0 has-focus-visible:border-none has-focus-visible:ring-0 has-focus-visible:ring-offset-0',
  // Error States
  'aria-invalid:border-none aria-invalid:ring-0',
);

const OTP_INPUT_SLOT_CLASSES = cn(
  BASE_INPUT_CLASSES,
  // Layout and Base
  'relative flex-none rounded-md bg-transparent text-center font-medium tabular-nums',
  // Focus Styles. A text slot takes the field ring, on `:focus`.
  'focus:border-primary focus:z-10',
  FIELD_FOCUS_RING_ALWAYS_CLASSES,
  // Error States. The stacked variants outrank the focus pair above, which
  // otherwise ties with the plain error rule and is emitted after it.
  'group-data-[state=error]:border-danger group-data-[state=error]:ring-danger',
  'group-data-[state=error]:focus:border-danger group-data-[state=error]:focus:ring-danger',
  // Transitions
  'transition-[color,box-shadow,border-color]',
);

// Popover Content Classes
const POPUP_CONTENT_CLASSES = cn(
  // Appearance
  'bg-popover text-popover-foreground rounded-md border shadow-elevation-2',
  // Layout and Base
  'flex flex-col gap-y-0.5',
  // Animations
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
  // Positioning
  'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
  // Z-Index
  'z-50',
);

// Dropdown Menu Content Classes
const CONTENT_CLASSES = cn(
  POPUP_CONTENT_CLASSES,
  // Sizing
  'max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-32',
  // Transform Origin
  'origin-[var(--radix-dropdown-menu-content-transform-origin)]',
  // Overflow
  'overflow-x-hidden overflow-y-auto',
  // Spacing
  'p-1',
  // Appearance: outer radius = item radius (4px) + padding (4px)
  'border rounded-lg',
);

// Sub-Menu Content Classes
const SUB_CONTENT_CLASSES = cn(
  // Base Popover Styles
  POPUP_CONTENT_CLASSES,
  // Sizing
  'min-w-32',
  // Transform Origin
  'origin-[var(--radix-dropdown-menu-content-transform-origin)]',
  // Overflow
  'overflow-hidden',
  // Spacing
  'p-1',
  // Appearance: outer radius = item radius (4px) + padding (4px)
  'border rounded-lg',
);

// Shared Item Classes
const SHARED_ITEM_CLASSES = cn(
  // Layout
  'relative flex items-center gap-2',
  // Appearance
  'rounded-sm px-2 py-1.5 text-sm select-none',
  // Interactivity
  'cursor-pointer outline-hidden data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
  // SVG Styles
  '[&_svg]:shrink-0',
  // Focus, Hover and Press States. `active:` is the only feedback a tap gets.
  'focus:bg-neutral-muted/50 focus:text-neutral-muted-foreground hover:bg-neutral-muted/50 hover:text-neutral-muted-foreground active:bg-neutral-muted-hover active:text-neutral-muted-foreground',
  // Checked and Selected States. Emitted after `active:`, so press is restated.
  'data-[state=checked]:bg-neutral-muted/70 data-[state=checked]:text-neutral-muted-foreground data-[selected=true]:bg-neutral-muted/70 data-[selected=true]:text-neutral-muted-foreground',
  'data-[state=checked]:active:bg-neutral-muted-hover data-[selected=true]:active:bg-neutral-muted-hover',
);

// Menu Item Classes
const ITEM_CLASSES = cn(
  // Base Shared Styles
  SHARED_ITEM_CLASSES,

  // Destructive Variant.
  'data-[variant=danger]:text-danger data-[variant=danger]:focus:bg-danger-muted data-[variant=danger]:focus:text-danger-muted-foreground data-[variant=danger]:*:[svg]:!text-danger',
  // Inset Spacing
  'data-[inset]:ps-8',
);

// Indicator Classes
const INDICATOR_CLASSES = cn(
  ITEM_CLASSES,
  // Appearance
  'rounded-sm py-1.5 pe-2 ps-8 data-[hidden-indicator=true]:ps-2 text-sm select-none',
  // Interactivity
  'cursor-pointer outline-hidden data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  // SVG Styles
  '[&_svg]:pointer-events-none [&_svg]:shrink-0',
);

// Shared default classes, grouped
const SHARED_ACTIVE_ELEMENT_CLASSES = cn(
  // Layout and Base
  'inline-flex items-center justify-center',
  // Border
  'border border-transparent',
  // Text
  'font-medium',
  // Transitions
  'transition-colors',
  // Error States. The border is otherwise transparent, so this is the signal.
  'aria-invalid:border-danger',
  // SVG Styles
  '[&_svg]:shrink-0',
);

// Component-specific default classes, grouped
const CHIP_DEFAULT_CLASSES = cn(
  SHARED_ACTIVE_ELEMENT_CLASSES,
  // Layout and Base
  'max-w-fit shrink-0',
  // Spacing
  'px-2.5 py-0.5 gap-1.5',
  // Text
  'text-xs',
  // Border and Shape
  'rounded-md',
);

const BADGE_DEFAULT_CLASSES = cn(
  SHARED_ACTIVE_ELEMENT_CLASSES,
  // Layout and Base
  'max-w-fit',
  // Spacing
  'px-2.5 py-0.5 gap-1.5',
  // Text
  'text-xs',
  // Border and Shape
  'rounded-full',
);

const BUTTON_DEFAULT_CLASSES = cn(
  SHARED_ACTIVE_ELEMENT_CLASSES,
  // Layout and Base
  'cursor-pointer',
  // Spacing
  'px-3 gap-2',
  // Text
  'text-sm whitespace-nowrap',
  // Border and Shape
  'rounded-md',
  // Disabled Styles
  'disabled:pointer-events-none disabled:opacity-50 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
  // Press Feedback
  PRESS_FEEDBACK_CLASSES,
  // Focus and Error States. The wider offset overrides the shared 1px.
  'outline-hidden focus:outline-hidden focus-visible:outline-hidden',
  FOCUS_RING_CLASSES,
  'focus-visible:ring-offset-2',
  'aria-invalid:ring-danger',
  // SVG Styles
  '[&_svg]:pointer-events-none',
);

const LINK_DEFAULT_CLASSES = cn(
  SHARED_ACTIVE_ELEMENT_CLASSES,
  // Layout and Base
  'cursor-pointer',
  // Transitions
  'transition-colors',
  // Focus Styles. Wider offset, as Button.
  'outline-hidden focus-visible:outline-hidden',
  FOCUS_RING_CLASSES,
  'focus-visible:ring-offset-2',
  // Disabled Styles
  'disabled:pointer-events-none disabled:opacity-50 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
  // Text
  'font-medium no-underline',
);

const TOOLTIP_CLASSES = cn(
  // Appearance
  'bg-neutral-muted text-neutral-muted-foreground rounded-md border border-neutral-muted',
  // Layout and Base
  'px-2 py-1 text-center text-sm max-w-60',
  // Overflow
  'overflow-hidden',
  // Animations
  'data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 ease-out-quint',
  // Transform Origin
  'origin-(--radix-tooltip-content-transform-origin)',
  // Positioning
  'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
  // Z-Index
  'z-50',
);

const CARD_DEFAULT_CLASSES = cn('rounded-md border group group/card');

export {
  ACTION_TONE_RING_CLASSES,
  BADGE_DEFAULT_CLASSES,
  BUTTON_DEFAULT_CLASSES,
  CARD_DEFAULT_CLASSES,
  CHECKBOX_CLASSES,
  CHIP_DEFAULT_CLASSES,
  CONTENT_CLASSES,
  FIELD_FOCUS_RING_ALWAYS_CLASSES,
  FIELD_FOCUS_RING_CLASSES,
  FOCUS_OUTLINE_COLOR_CLASSES,
  FOCUS_RING_ALWAYS_CLASSES,
  FOCUS_RING_CLASSES,
  FOCUS_RING_COLOR_CLASSES,
  FOCUS_RING_TONE_CLASSES,
  FOCUS_WITHIN_OUTLINE_COLOR_CLASSES,
  FOCUS_WITHIN_RING_CLASSES,
  INDICATOR_CLASSES,
  INPUT_CLASS_OVERRIDES,
  INPUT_CLASSES,
  INPUT_CONTAINER_CLASSES,
  ITEM_CLASSES,
  LINK_DEFAULT_CLASSES,
  OTP_INPUT_SLOT_CLASSES,
  POPUP_CONTENT_CLASSES,
  PRESS_FEEDBACK_CLASSES,
  RADIO_GROUP_ITEM_CLASSES,
  SHARED_ITEM_CLASSES,
  SUB_CONTENT_CLASSES,
  TOOLTIP_CLASSES,
};
